import { useCallback, useContext, useEffect, useRef } from "react";

import { GET_CLUB_BILLING_STATUS } from "../graphql/billingGraphql";
import { useBillingGate } from "../context/BillingGateContext";
import { useTranslation } from "../context/Internationalization";
import { SnackBarContext } from "../context/SnackBarContext";
import useGraphql from "./useGraphql";
import useLog from "./useLog";

export default function useClubBillingStatus() {
  const {
    state,
    beginBillingRequest,
    completeBillingRequest,
    failBillingRequest,
    resetBilling,
  } = useBillingGate();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const { t } = useTranslation();
  const snackBar = useContext(SnackBarContext);

  const dependenciesRef = useRef({ axiosGraphQL, errorLog, t, snackBar });
  useEffect(() => {
    dependenciesRef.current = { axiosGraphQL, errorLog, t, snackBar };
  }, [axiosGraphQL, errorLog, snackBar, t]);

  const loadBillingStatus = useCallback(
    async (clubId, { notifyOnError = false } = {}) => {
      if (!clubId) {
        resetBilling();
        return null;
      }

      const normalizedClubId = String(clubId);
      const requestId = beginBillingRequest(normalizedClubId);
      const current = dependenciesRef.current;

      try {
        const response = await current.axiosGraphQL({
          query: GET_CLUB_BILLING_STATUS,
          variables: { clubId: normalizedClubId },
        });
        const billing = response?.data?.data?.getClubBillingStatus || null;

        if (!billing || String(billing.clubId) !== normalizedClubId) {
          throw new Error(current.t("error_loading_billing"));
        }

        completeBillingRequest(requestId, normalizedClubId, billing);
        return billing;
      } catch (error) {
        const accepted = failBillingRequest(requestId, normalizedClubId, error);
        current.errorLog(
          error,
          "useClubBillingStatus",
          "loadBillingStatus",
          "GET_CLUB_BILLING_STATUS"
        );

        if (accepted && notifyOnError && current.snackBar) {
          current.snackBar.setOptions({
            alertMessage: error?.message || current.t("error_loading_billing"),
            alertSeverity: "error",
          });
          current.snackBar.setSnackbarOpen(true);
        }

        return null;
      }
    },
    [
      beginBillingRequest,
      completeBillingRequest,
      failBillingRequest,
      resetBilling,
    ]
  );

  return {
    state,
    resetBilling,
    loadBillingStatus,
  };
}
